// The pptx / download composite for the menu heatmap, composed from DATA (D8): no DOM
// clone, no mounted chart required — the collapsed / below-the-fold / map-none outputs
// stay buildable. The serialized shape pins parity with the retired coffee `formatSVG`.

import * as d3 from 'd3'

import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT } from '@aytm/lib/common/assets'
import { canvasToFile, svgToCanvas } from '@aytm/app/stats/lib/render/svg-export'

import { DEFAULT_CONTAINER_WIDTH } from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/constants'
import {
  getHeatmapData,
  hasOverlayData
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/overlayData'
import { drawOverlay } from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/render'

const SVG_NS = 'http://www.w3.org/2000/svg'
const XLINK_NS = 'http://www.w3.org/1999/xlink'

// size cap the coffee `loadBase64` used — the asset service downsizes huge menu scans
const BASE64_LIMIT = 1500

// replaces the coffee `imgBase64` model stash: one fetch per image path per page life
const base64Cache = new Map()

// size-capped, white-backed base64 of the menu image; resolves undefined on any failure
// (the composite then ships overlay-only, exactly as the coffee fetch-failure path did)
export const loadMenuImageBase64 = async image => {
  const path = image?.original?.path
  if (!path) return
  if (base64Cache.has(path)) return base64Cache.get(path)

  try {
    const url = getImageAssetPath({
      filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
      width: BASE64_LIMIT,
      height: BASE64_LIMIT,
      path,
      backgroundColor: 'white',
      json: true
    })
    if (!url) return

    const meta = await (await fetch(url)).json()
    if (!meta?.url) return

    const blob = await (await fetch(meta.url)).blob()
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

    base64Cache.set(path, base64)
    return base64
  } catch {
    return
  }
}

export const getMenuExportSize = image =>
  image?.original ? { width: image.original.width, height: image.original.height } : null

// standalone SVG string: base64 <image> underlay first, overlay on top — or null with no
// image schema. `withOverlay: false` is the caller's collapsed / chart-area-off signal.
export const composeMenuExportSvg = ({ id, image, answer, sections, menuViewMode, imgBase64, withOverlay = true }) => {
  if (!image?.original) return null

  const width = image.original.width
  const height = image.original.height

  const svgParent = document.createElementNS(SVG_NS, 'svg')
  svgParent.setAttribute('version', '1.1')
  // declared via setAttributeNS so the serializer emits proper xlink:href on the image
  // node (plain setAttribute leaves the prefix unregistered); the default xmlns comes
  // from createElementNS — the serialized root matches the coffee formatSVG output
  svgParent.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:xlink', XLINK_NS)
  svgParent.setAttribute('height', height)
  svgParent.setAttribute('width', width)
  svgParent.setAttribute('viewBox', `0 0 ${width} ${height}`)
  svgParent.style.opacity = '1'
  svgParent.style.zIndex = '1'

  if (imgBase64) {
    const imageNode = document.createElementNS(SVG_NS, 'image')
    imageNode.setAttribute('width', `${width}px`)
    imageNode.setAttribute('height', `${height}px`)
    imageNode.setAttribute('style', 'position: absolute; top: 0; left: 0;')
    imageNode.setAttributeNS(XLINK_NS, 'xlink:href', imgBase64)
    svgParent.appendChild(imageNode)
  }

  if (withOverlay) {
    const heatmap = getHeatmapData(answer, menuViewMode)

    if (hasOverlayData({ menuViewMode, heatmap, sections })) {
      // drawn detached at the on-screen scale formula, then re-sized to the full image via
      // attributes — the coffee clone treatment; drawing at scale 1 would shrink the
      // scale-compensated dots/strokes relative to what the live chart exported
      const scale = Math.min(1, DEFAULT_CONTAINER_WIDTH / width)
      const container = document.createElement('div')
      const overlay = drawOverlay(d3.select(container), {
        id,
        mode: menuViewMode,
        heatmap,
        sections,
        width,
        height,
        scale
      }).node()

      overlay.setAttribute('width', width)
      overlay.setAttribute('height', height)
      overlay.style.zIndex = '100'
      overlay.style.overflow = 'visible'
      svgParent.appendChild(overlay)
    }
  }

  return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgParent)
}

// in-view download: compose → rasterize → file, fully React-side (hm ActionButtons /
// sh downloadShelf shape); the coffee bridge stays pptx-only
export const downloadMenuChart = async (fileType, { id, image, answer, sections, menuViewMode, questionKey }) => {
  const imgBase64 = await loadMenuImageBase64(image)
  const svg = composeMenuExportSvg({ id, image, answer, sections, menuViewMode, imgBase64 })
  if (!svg) return

  const canvas = await svgToCanvas(svg, image.original.width, image.original.height)
  if (canvas) canvasToFile(canvas, fileType, questionKey, 'export_heatmap')
}
